import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import _ from 'lodash';
import WizardStepContainer from '../WizardStepContainer';
import { WizardStylesContext } from '../WizardStylesContext';
import WizardStepButtons from '../WizardStepButtons';
import ChoicePills from '../../Buttons/ChoicePills';
import {
  editorEmpty,
  focusEditor,
  getQuillStoredState,
  resetEditor,
  storeState
} from '../../TextEditors/Utilities/CoreUtils';
import { HASH_MENTION_CHARS, useEditor } from '../../TextEditors/quillHooks';
import { convertDescription } from '../../../utils/stringFunctions';
import { addPlanningInvestible } from '../../../api/investibles';
import {
  formInvestibleLink,
  formMarketAddInvestibleLink,
  navigate
} from '../../../utils/marketIdPathFunctions';
import { processTextAndFilesForSave } from '../../../api/files';
import { refreshInvestibles } from '../../../contexts/InvestibesContext/investiblesContextHelper';
import { InvestiblesContext } from '../../../contexts/InvestibesContext/InvestiblesContext';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { createJobNameFromComments } from '../../../pages/Dialog/Planning/userUtils';
import { getAcceptedStage } from '../../../contexts/MarketStagesContext/marketStagesContextHelper';
import { MarketStagesContext } from '../../../contexts/MarketStagesContext/MarketStagesContext';
import { addCommentsToMarket } from '../../../contexts/CommentsContext/commentsContextHelper';
import { CommentsContext } from '../../../contexts/CommentsContext/CommentsContext';
import { extractTodosList, removeTodosFromDescription } from '../../../utils/commentFunctions';
import { useHotkeys } from 'react-hotkeys-hook';
import { getGroupPresences } from '../../../contexts/MarketPresencesContext/marketPresencesHelper';
import { GroupMembersContext } from '../../../contexts/GroupMembersContext/GroupMembersContext';
import { TODO_TYPE } from '../../../constants/comments';
import NamePreviewBar, { useNamePreview } from '../../TextFields/NamePreviewBar';

const JOB_TYPE_HOTKEYS = ['ctrl+alt+1', 'ctrl+alt+2', 'ctrl+alt+3', 'ctrl+alt+4'];

function JobDescriptionStep (props) {
  const { marketId, groupId, updateFormData = () => {}, onFinish, roots, formData = {}, jobType, startOver, nextStep,
    moveFromComments, isSingleUser, myPresenceId, presences } = props;
  const history = useHistory();
  const intl = useIntl();
  const [marketStagesState] = useContext(MarketStagesContext);
  const [commentState, commentDispatch] = useContext(CommentsContext);
  const [groupPresencesState] = useContext(GroupMembersContext);
  const groupPresences = getGroupPresences(presences, groupPresencesState, marketId, groupId) || [];
  const myGroupPresence = groupPresences.find((presence) => presence.id === myPresenceId);
  const isMovingTasks = !_.isEmpty(roots);
  const editorName = isMovingTasks ? `addJobWizard${roots[0].id}` : `addJobWizard${groupId}`;
  const { newQuantity } = formData;
  const showImmediate = !_.isEmpty(myGroupPresence)||!isSingleUser;
  const jobTypes = ['APPROVABLE', ...(showImmediate ? ['IMMEDIATE'] : []), 'READY', 'NOT_READY'];

  function getDefaultDescription() {
    let defaultDescription = undefined;
    if (_.isEmpty(getQuillStoredState(editorName))&&isMovingTasks) {
      const isBugMove = roots.find((fromComment) => fromComment.comment_type === TODO_TYPE && !fromComment.investible_id);
      if (!isBugMove) {
        // If moving more than one non bug task don't default the description
        if (_.size(roots) === 1) {
          const fromComment = roots[0];
          const { body } = fromComment || {};
          // No need to clip to 80 here as that will happen when save
          const { name } = convertDescription(body, 200);
          defaultDescription = name;
        }
      } else {
        defaultDescription = createJobNameFromComments(roots, intl);
      }
    }
    return defaultDescription;
  }

  const defaultDescription = getDefaultDescription();

  if (!_.isEmpty(defaultDescription)) {
    storeState(editorName, `<p>${defaultDescription}</p>`);
  }
  const [hasValue, setHasValue] = useState(!editorEmpty(getQuillStoredState(editorName)));
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [, investiblesDispatch] = useContext(InvestiblesContext);
  const classes = useContext(WizardStylesContext);
  const { name: namePreview, updateName, refreshName } = useNamePreview(editorName);

  const editorSpec = {
    placeholder: "Ex: Make magic happen. A bullet list in this description will be become tasks.",
    value: getQuillStoredState(editorName),
    marketId,
    mentionsAllowed: true,
    mentionDenotationChars: HASH_MENTION_CHARS,
    autoFocus: true,
    maxHeight: '300px',
    onUpload: setUploadedFiles,
    onChange: () => { setHasValue(!editorEmpty(getQuillStoredState(editorName))); updateName(); },
  };

  const [Editor] = useEditor(editorName, editorSpec);

  // Tracks whether the most recent radio change came from an arrow key, so we
  // don't steal focus to the editor and break keyboard navigation through the
  // RadioGroup.
  const arrowKeyChangeRef = useRef(false);
  function onChange(event) {
    updateFormData({
      newQuantity: event.target.value
    });
    if (!arrowKeyChangeRef.current) {
      focusEditor(editorName);
    }
  }
  function doIncrement(resolved) {
    if (resolved?.isMissingName) {
      nextStep();
    } else if (currentValue === 'IMMEDIATE' && !isSingleUser) {
      nextStep(2);
    }
  }

  const hasFromComments = _.size(roots) > 0;
  function onTerminate() {
    let checkedString;
    roots.forEach((comment) => {
      if (checkedString) {
        checkedString += `&fromCommentId=${comment.id}`;
      } else {
        checkedString = `&fromCommentId=${comment.id}`;
      }
    });
    startOver();
    navigate(history, `${formMarketAddInvestibleLink(marketId, groupId)}${checkedString}`);
  }

  // T-all-2191 (same class): the open backlog section preselects the matching pill. Coerce to a string so
  // this works whether the index arrives as a number (inline launch) or a string (full-screen /wizard URL).
  const defaultFromPage = jobType === undefined ? 'APPROVABLE' : (`${jobType}` === '0' ? 'READY' : 'NOT_READY');
  const currentValue = newQuantity || defaultFromPage || '';

  function createJob() {
    const isApprovable = currentValue === 'APPROVABLE';
    const readyToStart = currentValue === 'READY' ? true :
      (currentValue === 'NOT_READY' ? false : undefined);
    const {
      uploadedFiles: filteredUploads,
      text: tokensRemoved,
    } = processTextAndFilesForSave(uploadedFiles, getQuillStoredState(editorName));
    const { name, description} = convertDescription(tokensRemoved);
    if (_.isEmpty(name)) {
      updateFormData({
        description,
        uploadedFiles: filteredUploads,
        jobStage: currentValue
      });
      resetEditor(editorName);
      refreshName();
      return Promise.resolve({isMissingName: true});
    }
    const addInfo = {
      name,
      description,
      groupId,
      marketId,
      uploadedFiles: filteredUploads
    }
    const todos = extractTodosList(tokensRemoved);
    if (!_.isEmpty(todos)) {
      addInfo.todos = todos;
      const newDescription = removeTodosFromDescription(description);
      addInfo.description = newDescription;
    }
    if (isApprovable) {
      addInfo.assignments = [myPresenceId];
    }
    else if (readyToStart !== undefined) {
      addInfo.openForInvestment = readyToStart;
    }
    else if (isSingleUser) {
      addInfo.stageId = getAcceptedStage(marketStagesState, marketId).id;
      addInfo.assignments = [myPresenceId];
    }
    return addPlanningInvestible(addInfo)
      .then((result) => {
        let inv = result;
        if (!_.isEmpty(addInfo.todos)) {
          const { investible, todos } = result;
          addCommentsToMarket(todos, commentState, commentDispatch);
          inv = investible;
        }
        refreshInvestibles(investiblesDispatch, () => {}, [inv]);
        const { id: investibleId } = inv.investible;
        let link = formInvestibleLink(marketId, investibleId);
        // reset the editor box
        resetEditor(editorName);
        // update the form data with the saved investible
        updateFormData({
          investibleId,
          link
        });
        const shouldFinish = currentValue !== 'IMMEDIATE' || isSingleUser;
        if (moveFromComments) {
          return moveFromComments(inv, formData, updateFormData).then(() => {
            if (shouldFinish) {
              return onFinish({
                link
              });
            }
            return {link};
          });
        }
        if (shouldFinish) {
          return onFinish({
            link
          });
        }
        return {link};
      })
  }

  function simulatePriority(key) {
    return () => {
      const target = {value: key};
      onChange({target});
    };
  }
  useHotkeys(JOB_TYPE_HOTKEYS[0], simulatePriority(jobTypes[0]), {enableOnContentEditable: true},
    [showImmediate]);
  useHotkeys(JOB_TYPE_HOTKEYS[1], simulatePriority(jobTypes[1]), {enableOnContentEditable: true},
    [showImmediate]);
  useHotkeys(JOB_TYPE_HOTKEYS[2], simulatePriority(jobTypes[2]), {enableOnContentEditable: true},
    [showImmediate]);
  useHotkeys(JOB_TYPE_HOTKEYS[3], simulatePriority(jobTypes[3]), {enabled: showImmediate,
      enableOnContentEditable: true}, [showImmediate]);

  return (
    <WizardStepContainer
      {...props}
      isLarge
    >
      <Typography className={classes.introText}>
        How would you describe this job?
      </Typography>
      <Typography className={classes.introSubText} variant="subtitle1" style={{marginBottom: 0}}>
        Fill the description below and the job name will be created automatically. A bullet list will become tasks.
      </Typography>
      <ChoicePills
        ariaLabel="add-vote-certainty"
        value={currentValue}
        onChange={(value) => onChange({ target: { value } })}
        options={jobTypes.map((certainty) => {
          // Label is "Name - description"; pill shows the name, and the hover
          // shows the explanatory remainder with the keystroke in parentheses
          // after it (S-1 / C-all-979).
          const full = intl.formatMessage({ id: `jobTypeLabel${certainty}` });
          const sep = full.indexOf(' - ');
          const remainder = sep >= 0 ? full.slice(sep + 3) : '';
          const keys = JOB_TYPE_HOTKEYS[jobTypes.indexOf(certainty)];
          return {
            value: certainty,
            id: `${certainty}`,
            label: sep >= 0 ? full.slice(0, sep) : full,
            tooltip: remainder ? `${remainder} (${keys})` : `(${keys})`,
          };
        })}
      />
      <NamePreviewBar name={namePreview} />
      {Editor}
      <div className={classes.borderBottom} />
      <WizardStepButtons
        {...props}
        validForm={hasValue}
        nextLabel='jobCreate'
        onNext={createJob}
        onIncrement={doIncrement}
        showTerminate={hasFromComments}
        onTerminate={onTerminate}
        terminateLabel='JobWizardStartOver'
      />
    </WizardStepContainer>
  );
}

JobDescriptionStep.propTypes = {
  updateFormData: PropTypes.func,
  formData: PropTypes.object
};

export default JobDescriptionStep;
